// Settings sheet for managing user-provided AI provider keys (BYOK).
// Triggered by the unified menu via the `open-byok-keys` window event.
// Keys are stored server-side; the encrypted column is never SELECTed by
// the client (column-level grant excludes it).
import { useEffect, useState, useCallback } from "react";
import { Key, Trash2, Plus, X, Eye, EyeOff } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const PROVIDERS = [
  { id: "openai",     label: "OpenAI",     hint: "sk-...",       link: "https://platform.openai.com/api-keys" },
  { id: "anthropic",  label: "Anthropic",  hint: "sk-ant-...",   link: "https://console.anthropic.com/settings/keys" },
  { id: "openrouter", label: "OpenRouter", hint: "sk-or-...",    link: "https://openrouter.ai/keys" },
  { id: "xai",        label: "xAI (Grok)", hint: "xai-...",      link: "https://console.x.ai/" },
  { id: "groq",       label: "Groq",       hint: "gsk_...",      link: "https://console.groq.com/keys" },
  { id: "mistral",    label: "Mistral",    hint: "...",          link: "https://console.mistral.ai/api-keys/" },
] as const;

type ProviderId = typeof PROVIDERS[number]["id"];

type KeyRow = {
  id: string;
  provider: ProviderId;
  label: string | null;
  key_last4: string | null;
  created_at: string;
};

export default function ByokKeysSheet() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [keys, setKeys] = useState<KeyRow[]>([]);
  const [provider, setProvider] = useState<ProviderId>("openai");
  const [value, setValue] = useState("");
  const [label, setLabel] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener("open-byok-keys", onOpen);
    return () => window.removeEventListener("open-byok-keys", onOpen);
  }, []);

  const refresh = useCallback(async () => {
    if (!user) return;
    const { data, error } = await supabase
      .from("user_api_keys")
      .select("id, provider, label, key_last4, created_at")
      .order("created_at", { ascending: false });
    if (!error && data) setKeys(data as KeyRow[]);
  }, [user]);

  useEffect(() => { if (open) refresh(); }, [open, refresh]);

  const save = async () => {
    if (!user) { toast.error("Sign in first"); return; }
    const v = value.trim();
    if (v.length < 8) { toast.error("That doesn't look like an API key"); return; }
    setSaving(true);
    const { error } = await supabase.from("user_api_keys").insert({
      user_id: user.id,
      provider,
      label: label.trim() || null,
      encrypted_key: v,
      key_last4: v.slice(-4),
    });
    setSaving(false);
    if (error) { toast.error(error.message); return; }
    toast.success(`${PROVIDERS.find((p) => p.id === provider)?.label} key saved`);
    setValue(""); setLabel(""); setShowKey(false);
    refresh();
    window.dispatchEvent(new Event("byok-keys-changed"));
  };

  const remove = async (id: string) => {
    const { error } = await supabase.from("user_api_keys").delete().eq("id", id);
    if (error) { toast.error(error.message); return; }
    setKeys((arr) => arr.filter((k) => k.id !== id));
    window.dispatchEvent(new Event("byok-keys-changed"));
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="right" className="w-full sm:max-w-[480px] p-0 flex flex-col">
        <SheetHeader className="p-4 border-b border-border">
          <SheetTitle className="font-mono text-sm flex items-center gap-2">
            <Key className="w-4 h-4 text-primary" /> Your AI keys
          </SheetTitle>
          <p className="text-[11px] text-muted-foreground font-mono">
            Add your own provider keys to unlock more models in Compare mode. Keys are stored encrypted server-side and never sent to your browser.
          </p>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto p-4 space-y-6">
          <section className="space-y-2">
            <div className="text-[10px] uppercase tracking-wider font-mono text-muted-foreground/70">Add a key</div>
            <div className="grid grid-cols-2 gap-1.5">
              {PROVIDERS.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setProvider(p.id)}
                  className={cn(
                    "h-9 px-2 rounded-lg border text-xs font-mono transition-colors",
                    provider === p.id ? "border-primary bg-primary/10 text-foreground" : "border-border text-muted-foreground hover:border-primary/50",
                  )}
                >
                  {p.label}
                </button>
              ))}
            </div>
            <a
              href={PROVIDERS.find((p) => p.id === provider)?.link}
              target="_blank"
              rel="noreferrer"
              className="block text-[10px] text-muted-foreground font-mono underline-offset-2 hover:underline"
            >
              Get a {PROVIDERS.find((p) => p.id === provider)?.label} key →
            </a>
            <div className="relative">
              <input
                type={showKey ? "text" : "password"}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder={PROVIDERS.find((p) => p.id === provider)?.hint}
                className="w-full h-10 pl-3 pr-10 rounded-lg bg-card border border-border text-sm font-mono outline-none focus:border-primary/60"
                autoComplete="off"
              />
              <button
                type="button"
                onClick={() => setShowKey((s) => !s)}
                className="absolute right-1 top-1 h-8 w-8 grid place-items-center rounded-md text-muted-foreground hover:text-foreground"
                aria-label={showKey ? "Hide key" : "Show key"}
              >
                {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Label (optional, e.g. Personal)"
              className="w-full h-9 px-3 rounded-lg bg-card border border-border text-xs font-mono outline-none focus:border-primary/60"
            />
            <button
              type="button"
              onClick={save}
              disabled={saving || !value.trim()}
              className={cn(
                "h-10 w-full inline-flex items-center justify-center gap-2 rounded-lg font-mono text-xs font-semibold transition-all",
                value.trim() ? "bg-primary text-primary-foreground hover:opacity-90" : "bg-secondary text-muted-foreground cursor-not-allowed",
              )}
            >
              <Plus className="w-4 h-4" /> {saving ? "Saving..." : "Save key"}
            </button>
          </section>

          <section className="space-y-2">
            <div className="text-[10px] uppercase tracking-wider font-mono text-muted-foreground/70">Saved keys</div>
            {!user ? (
              <div className="text-xs text-muted-foreground font-mono">Sign in to save keys.</div>
            ) : keys.length === 0 ? (
              <div className="text-xs text-muted-foreground font-mono">No keys yet.</div>
            ) : (
              <div className="space-y-1.5">
                {keys.map((k) => (
                  <div key={k.id} className="flex items-center gap-2 p-2 rounded-lg border border-border bg-card">
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-mono">
                        {PROVIDERS.find((p) => p.id === k.provider)?.label ?? k.provider}
                        {k.label && <span className="text-muted-foreground"> · {k.label}</span>}
                      </div>
                      <div className="text-[10px] font-mono text-muted-foreground">
                        •••• {k.key_last4 ?? "????"} · added {new Date(k.created_at).toLocaleDateString()}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => remove(k.id)}
                      className="h-7 w-7 grid place-items-center rounded-md text-muted-foreground hover:text-destructive hover:bg-secondary/70"
                      aria-label="Delete key"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>

        <div className="border-t border-border p-3 flex justify-end">
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="h-8 px-3 inline-flex items-center gap-1 rounded-md text-xs font-mono text-muted-foreground hover:text-foreground hover:bg-secondary/70"
          >
            <X className="w-3.5 h-3.5" /> Close
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}